import { MapPin, Video, CheckCircle2, Globe, Mail, Calendar, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export function initials(name) {
  const parts = String(name || 'T').trim().split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p.replace(/[^A-Za-z]/g, '')[0] || '').join('');
  return (letters || 'T').toUpperCase();
}

export function TherapistCard({ t }) {
  const modality = (t.modality || (t.telehealth ? 'telehealth' : '')).toLowerCase();
  const states = t.states || (t.state ? [t.state] : []);
  const booking = t.booking_url || t.website;
  return (
    <div className="bg-white rounded-3xl p-6 card-soft flex flex-col h-full" data-testid={`therapist-card-${t.id}`}>
      <div className="flex items-start gap-4">
        {t.photo ? (
          <img src={t.photo} alt={`Headshot of ${t.name}`} loading="lazy"
            className="w-20 h-20 rounded-full object-cover ring-2 ring-gold/40 shadow-sm shrink-0"/>
        ) : (
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-beige to-gold/45 text-navy flex items-center justify-center font-heading text-2xl ring-2 ring-gold/30 shadow-sm shrink-0">
            {initials(t.name)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-heading text-lg leading-tight">{t.name}</p>
          {t.credentials && <p className="text-xs text-navy/55 mt-0.5">{t.credentials}</p>}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(modality === 'both' || modality === 'telehealth') && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-gold/15 text-navy rounded-full px-2 py-0.5"><Video size={10}/> Telehealth</span>
            )}
            {(modality === 'both' || modality === 'in-person') && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-beige text-navy/70 rounded-full px-2 py-0.5"><MapPin size={10}/> In-person</span>
            )}
            {t.faith_informed && <span className="text-[10px] uppercase tracking-wide bg-beige text-navy/70 rounded-full px-2 py-0.5">Faith-informed</span>}
            {t.trauma_informed && <span className="text-[10px] uppercase tracking-wide bg-beige text-navy/70 rounded-full px-2 py-0.5">Trauma-informed</span>}
          </div>
        </div>
      </div>

      {t.bio && <p className="text-sm text-navy/70 mt-4 leading-relaxed line-clamp-4">{t.bio}</p>}

      {!!(t.specialties || []).length && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {t.specialties.map((s) => <span key={s} className="text-[11px] bg-navy/5 text-navy/70 rounded-full px-2.5 py-1">{s}</span>)}
        </div>
      )}

      <div className="mt-4 space-y-1.5 text-xs text-navy/60">
        {!!states.length && <p className="flex items-center gap-1.5"><MapPin size={12}/> Licensed: {states.join(', ')}</p>}
        {!!(t.languages || []).length && <p className="flex items-center gap-1.5"><Globe size={12}/> {t.languages.join(', ')}</p>}
        {!!(t.insurance || []).length && <p className="flex items-center gap-1.5"><CheckCircle2 size={12}/> {t.insurance.join(', ')}</p>}
      </div>

      <div className="mt-auto pt-4 flex flex-wrap items-center gap-2">
        {t.slug && (
          <Link to={`/therapists/${t.slug}`} data-testid={`therapist-profile-${t.id}`}
            className="inline-flex items-center gap-1.5 bg-navy text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-navy-soft transition-smooth">
            View profile <ArrowUpRight size={13}/>
          </Link>
        )}
        {booking && (
          <a href={booking} target="_blank" rel="noreferrer" data-testid={`therapist-book-${t.id}`}
            className="inline-flex items-center gap-1.5 bg-beige text-navy rounded-full px-4 py-2 text-xs font-medium hover:brightness-95 transition-smooth">
            <Calendar size={13}/> Book
          </a>
        )}
        {t.email && (
          <a href={`mailto:${t.email}`} className="inline-flex items-center gap-1.5 bg-beige text-navy rounded-full px-4 py-2 text-xs font-medium hover:brightness-95">
            <Mail size={13}/> Email
          </a>
        )}
      </div>
    </div>
  );
}
